import React, { useState, useRef, useEffect, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReportePDF from './reportePDF/ReportePDF';
import SummaryStatsCard, { SUMMARY_STATS_THEMES } from './SummaryStatsCard';
import './reportePDF/pdf.css';
import pdfCssString from './reportePDF/pdfCssString';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Chart } from 'primereact/chart';
import { Chart as ChartJS } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Carousel } from 'primereact/carousel';
import { Timeline } from 'primereact/timeline';
import CustomerService from '../service/CustomerService';
import { ROL_PERFIL_ADMINISTRADOR } from '../constants/roles';
import { getResumenGeneral, getGastosPorRubro, getGastosPorProveedor, getPersonasBarrio } from "../service/DashboardService";
import { enviarConTemplate, getMailTemplates } from "../service/MailTemplateService";
import MailTemplateEditor from "./MailTemplateEditor";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import esLocale from '@fullcalendar/core/locales/es';
import { BarrioContext } from "../components/BarrioContext";
import SinAccesoResidente from './SinAccesoResidente';
import { usePersonaResidente } from './PersonaResidenteContext';
import { useAuth } from "../components/AuthContext";
import { useTerminologia } from "../hooks/useTerminologia";
import { Calendar } from "primereact/calendar";
import { Toast } from 'primereact/toast';
import LoadingOverlay from './LoadingOverlay';
import { descargarComprobantesUnificados } from "../utilities/descargarComprobantes";
import { fetchLiquidacionPdfData, generarPdfBase64 } from "../utilities/liquidacionPdf";

const centerTextPlugin = {
    id: 'centerText',
    afterDraw(chart) {
        const opts = chart.config?.options?.plugins?.centerText;
        if (!opts?.display) return;
        const { ctx } = chart;
        const { left, right, top, bottom } = chart.chartArea;
        const cx = (left + right) / 2;
        const cy = (top + bottom) / 2;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '500 11px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#667085';
        ctx.fillText('Total gastos:', cx, cy - 14);
        const val = opts.value || '';
        ctx.font = `bold ${val.length > 13 ? 12 : 14}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = '#101828';
        ctx.fillText(val, cx, cy + 8);
        ctx.restore();
    }
};

ChartJS.register(ChartDataLabels, centerTextPlugin);

const visitorChart = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'],
    datasets: [
        {
            label: 'Plan',
            data: [630, 630, 695, 695, 695, 760, 760, 760, 840, 840, 840, 840],
            borderColor: ['#FC6161'],
            pointBorderColor: 'transparent',
            pointBackgroundColor: 'transparent',
            type: 'line',
            fill: false,
            barPercentage: 0.5,
            stepped: true
        },
        {
            label: 'Growth actual',
            data: [600, 671, 660, 665, 700, 610, 810, 790, 710, 860, 810, 780],
            backgroundColor: getComputedStyle(document.body).getPropertyValue('--primary-color'),
            fill: true,
            barPercentage: 0.5
        }
    ]
};

const visitorChartOptions = {
    plugins: {
        legend: { position: 'top', align: 'end' },
        datalabels: { display: false }
    },
    responsive: true,
    hover: {
        mode: 'index'
    },
    scales: {
        y: {
            min: 500,
            max: 900,
            grid: {
                display: false
            }
        },
        x: {
            grid: {
                display: false
            }
        }
    }
};

const countryChart = {
    labels: ['RUS', 'Other', 'IND', 'AUS', 'JPN', 'USA', 'CHN'],
    datasets: [
        {
            data: [30, 18, 36, 54, 61, 90, 72],
            backgroundColor: ['#0F8BFD', '#545C6B', '#EC4DBC', '#EEE500', '#FC6161', '#00D0DE', '#873EFE'],
            hoverBackgroundColor: ['#0F8BFD', '#545C6B', '#EC4DBC', '#EEE500', '#FC6161', '#00D0DE', '#873EFE'],
            borderColor: 'transparent',
            fill: true
        }
    ]
};

const countryChartOptions = {
    responsive: true,
    plugins: { datalabels: { display: false } }
};

const revenueChart = {
    labels: ['January', 'February', 'March', 'April', 'May', 'June', 'July'],
    datasets: [
        {
            label: 'Sales',
            data: [37, 34, 21, 27, 10, 18, 15],
            borderColor: '#EEE500',
            pointBackgroundColor: '#EEE500',
            backgroundColor: 'rgba(238, 229, 0, 0.05)',
            fill: true,
            tension: 0.4
        },
        {
            label: 'Revenue',
            data: [31, 27, 30, 37, 23, 29, 20],
            borderColor: '#00D0DE',
            pointBackgroundColor: '#00D0DE',
            backgroundColor: 'rgba(0, 208, 222, 0.05)',
            fill: true,
            tension: 0.4
        },
        {
            label: 'Expenses',
            data: [21, 7, 13, 3, 19, 11, 6],
            borderColor: '#FC6161',
            pointBackgroundColor: '#FC6161',
            backgroundColor: 'rgba(253, 72, 74, 0.05)',
            fill: true,
            tension: 0.4
        },
        {
            label: 'Customer',
            data: [47, 31, 35, 20, 46, 39, 25],
            borderColor: '#0F8BFD',
            pointBackgroundColor: '#0F8BFD',
            backgroundColor: 'rgba(15, 139, 253, 0.05)',
            fill: true,
            tension: 0.4
        }
    ]
};

const revenueChartOptions = {
    responsive: true,
    hover: { mode: 'index' },
    plugins: { datalabels: { display: false } },
    scales: {
        y: {
            min: 0,
            max: 50,
            ticks: {
                stepSize: 5
            }
        }
    }
};

const customerChart = {
    labels: ['January', 'March', 'May', 'Agust', 'October', 'December'],
    datasets: [
        { data: [10, 25, 48, 35, 54, 70], backgroundColor: '#AAABDD', hoverBackgroundColor: '#AAABDD', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [18, 35, 23, 30, 59, 65], backgroundColor: '#A0A0D9', hoverBackgroundColor: '#A0A0D9', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [20, 47, 46, 46, 61, 70], backgroundColor: '#ACADDE', hoverBackgroundColor: '#ACADDE', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [17, 34, 18, 48, 67, 68], backgroundColor: '#ABABDD', hoverBackgroundColor: '#ABABDD', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [9, 37, 47, 50, 60, 62], backgroundColor: '#A2A3D9', hoverBackgroundColor: '#A2A3D9', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [8, 48, 40, 52, 72, 75], backgroundColor: '#A3A4DA', hoverBackgroundColor: '#A3A4DA', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [10, 18, 50, 47, 63, 80], backgroundColor: '#A2A3D9', hoverBackgroundColor: '#A2A3D9', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [20, 36, 39, 58, 59, 85], backgroundColor: '#8485CD', hoverBackgroundColor: '#8485CD', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [30, 45, 35, 50, 54, 81], backgroundColor: '#7D7ECA', hoverBackgroundColor: '#7D7ECA', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [28, 35, 52, 56, 60, 77], backgroundColor: '#8384CD', hoverBackgroundColor: '#8384CD', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [40, 40, 38, 45, 68, 86], backgroundColor: '#8F90D2', hoverBackgroundColor: '#8F90D2', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [50, 23, 27, 34, 65, 90], backgroundColor: '#8C8DD0', hoverBackgroundColor: '#8C8DD0', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [29, 27, 29, 42, 55, 84], backgroundColor: '#9495D4', hoverBackgroundColor: '#9495D4', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [10, 37, 47, 29, 59, 80], backgroundColor: '#9696D4', hoverBackgroundColor: '#9696D4', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [10, 54, 42, 38, 63, 83], backgroundColor: '#7273C6', hoverBackgroundColor: '#7273C6', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [25, 44, 50, 56, 65, 92], backgroundColor: '#5F60BE', hoverBackgroundColor: '#5F60BE', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [30, 43, 48, 45, 73, 78], backgroundColor: '#5C5DBD', hoverBackgroundColor: '#5C5DBD', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 },
        { data: [29, 47, 54, 60, 77, 86], backgroundColor: '#5C5DBD', hoverBackgroundColor: '#5C5DBD', fill: true, categoryPercentage: 1.0, barPercentage: 1.0 }
    ]
};

const customerChartOptions = {
    interaction: { mode: 'x' },
    plugins: { legend: { display: false }, datalabels: { display: false } },
    scales: {
        y: { display: false },
        x: { grid: { display: false } }
    }
};

// Colores para gráficos de gastos
const CHART_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#ec4899', '#84cc16'];

// Ciclo de temas para las cards de gastos por proveedor (mismo componente que usa Gastos.js)
const SUMMARY_STATS_THEME_CYCLE = Object.keys(SUMMARY_STATS_THEMES);

const Dashboard = () => {
    const toast = useRef(null);
    const navigate = useNavigate();
    const HOST_API = process.env.REACT_APP_URL;
    const showSuccess = (titulo, detalle) =>
        toast.current?.show({ severity: 'success', summary: titulo, detail: detalle, life: 4000 });

    const [mailDialogVisible, setMailDialogVisible] = useState(false);
    const [mailAsunto, setMailAsunto] = useState('');
    const [mailHtmlTemplate, setMailHtmlTemplate] = useState('');
    const [mailEsDefault, setMailEsDefault] = useState(false);
    const [cargandoPlantillaMail, setCargandoPlantillaMail] = useState(false);
    const [enviandoMail, setEnviandoMail] = useState(false);

    const orderYear = [
        { name: '2021', code: '0' },
        { name: '2020', code: '1' }
    ];
    const visitorYear = [
        { name: '2020', code: '0' },
        { name: '2019', code: '1' }
    ];
    const customerYear = [
        { name: '2021', code: '0' },
        { name: '2022', code: '1' }
    ];
    const revenueMonth = [
        { name: 'January - July 2021', code: '0' },
        { name: 'August - December 2020', code: '1' }
    ];

    const [customersTable, setCustomersTable] = useState(null);
    const [customersTable1, setCustomersTable1] = useState(null);
    const [customersTable2, setCustomersTable2] = useState(null);
    const [selectedVisitorYear, setSelectedVisitorYear] = useState(visitorYear[0]);
    const [selectedRevenueMonth, setSelectedRevenueMonth] = useState(revenueMonth[0]);
    const [selectedOrderYear, setSelectedOrderYear] = useState(orderYear[0]);
    const [selectedCustomerYear, setSelectedCustomerYear] = useState(customerYear[0]);
    const [customerCarousel, setCustomerCarousel] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());

    const visitor = useRef(null);
    const customer = useRef(null);
    const revenue = useRef(null);
    const dt = useRef(null);

    let growth = '$620,076';
    let avgCustomer = '$1,120';

    const timelineEvents = [
        { transaction: 'Payment from #28492', amount: '+$250.00', date: 'June 13, 2020 11:09 AM', icon: 'pi pi-check', iconColor: '#0F8BFD', amountColor: '#00D0DE' },
        { transaction: 'Process refund to #94830', amount: '-$570.00', date: 'June 13, 2020 08:22 AM', icon: 'pi pi-refresh', iconColor: '#FC6161', amountColor: '#FC6161' },
        { transaction: 'New 8 user to #5849', amount: '+$50.00', date: 'June 12, 2020 02:56 PM', icon: 'pi pi-plus', iconColor: '#0BD18A', amountColor: '#0BD18A' },
        { transaction: 'Payment from #3382', amount: '+$3830.00', date: 'June 11, 2020 06:11 AM', icon: 'pi pi-check', iconColor: '#0F8BFD', amountColor: '#00D0DE' },
        { transaction: 'Payment from #4738', amount: '+$845.00', date: 'June 11, 2020 03:50 AM', icon: 'pi pi-check', iconColor: '#0F8BFD', amountColor: '#00D0DE' },
        { transaction: 'Payment failed form #60958', amount: '$1450.00', date: 'June 10, 2020 07:54 PM', icon: 'pi pi-exclamation-triangle', iconColor: '#EC4DBC', amountColor: '#EC4DBC' },
        { transaction: 'Payment from #5748', amount: '+$50.00', date: 'June 09, 2020 11:37 PM', icon: 'pi pi-check', iconColor: '#0F8BFD', amountColor: '#00D0DE' },
        { transaction: 'Removed 32 users from #5849', amount: '-$240.00', date: 'June 09, 2020 08:40 PM', icon: 'pi pi-minus', iconColor: '#FC6161', amountColor: '#FC6161' }
    ];

    const descargarPdf = async () => {
        try {
            const data = await fetchLiquidacionPdfData(barrio.id, administracionId, selectedDate);

            // Capturar el gráfico de torta como imagen base64
            let graficoPorGrupoImg = null;
            try {
                const ref = chartRubrosRef.current;
                if (ref) {
                    const chartInstance = typeof ref.getChart === 'function'
                        ? ref.getChart()
                        : ref.chart;
                    if (chartInstance?.toBase64Image) {
                        graficoPorGrupoImg = chartInstance.toBase64Image();
                    }
                }
            } catch { /* silencioso si el chart no está renderizado */ }

            setPdfData({ ...data, graficoPorGrupoImg });
        } catch (err) {
            console.error("Error al obtener datos para PDF:", err);
        }
        setPdfDialogVisible(true);
    };

    const carouselResponsiveOptions = [
        { breakpoint: '1024px', numVisible: 3, numScroll: 3 },
        { breakpoint: '768px', numVisible: 2, numScroll: 2 },
        { breakpoint: '560px', numVisible: 1, numScroll: 1 }
    ];

    let customerMax = '1232';
    let customerMin = '284';
    let customerAvg = '875';

    // ── PDF ──
    const pdfRef = useRef(null);
    const chartRubrosRef = useRef(null);
    const [pdfData, setPdfData] = useState(null);
    const [pdfDialogVisible, setPdfDialogVisible] = useState(false);
    const [descargandoComprobantes, setDescargandoComprobantes] = useState(false);
    const [mediosPagoVisible, setMediosPagoVisible] = useState(false);

    const copiarAlPortapapeles = (valor, etiqueta) => {
        if (!valor) return;
        navigator.clipboard.writeText(valor);
        showSuccess('Copiado', `${etiqueta} copiado al portapapeles`);
    };

    const handlePrint = () => {
        const content = pdfRef.current;
        if (!content) return;

        const win = window.open('', '_blank', 'width=900,height=700');
        if (!win) {
            alert('Permita ventanas emergentes en su navegador para imprimir el PDF.');
            return;
        }

        win.document.write(
            '<!DOCTYPE html><html><head><meta charset="utf-8">' +
            '<title>Liquidacion de Expensas</title>' +
            '<style>body{margin:0;padding:0;background:#fff;color:#222;font-family:Arial,sans-serif;}table{border-collapse:collapse;}</style>' +
            '<style>' + pdfCssString + '</style>' +
            '</head><body>' +
            content.innerHTML +
            '</body></html>'
        );
        win.document.close();

        let done = false;
        const doPrint = () => {
            if (done) return;
            done = true;
            win.focus();
            win.print();
        };
        win.addEventListener('load', doPrint, { once: true });
        setTimeout(doPrint, 800);
    };

    const handleDescargarComprobantes = async () => {
        setDescargandoComprobantes(true);
        try {
            const periodoStr = selectedDate ? selectedDate.toISOString().split("T")[0] : "";
            await descargarComprobantesUnificados({
                gastos: pdfData?.detalleGastos,
                hostApi: HOST_API,
                nombreArchivo: `Comprobantes_${barrio?.nombre || "barrio"}_${periodoStr}.pdf`,
                onError: (mensaje, severity = 'warn') => {
                    toast.current?.show({ severity, summary: "Descargar Comprobantes", detail: mensaje, life: 4000 });
                },
            });
        } catch (err) {
            console.error("Error al descargar comprobantes:", err);
            toast.current?.show({ severity: "error", summary: "Descargar Comprobantes", detail: "No se pudieron descargar los comprobantes", life: 3000 });
        } finally {
            setDescargandoComprobantes(false);
        }
    };

    const handleAbrirDialogMail = async () => {
        if (!barrio?.id) return;
        setCargandoPlantillaMail(true);
        try {
            const { data } = await getMailTemplates(administracionId, barrio.id);
            const envioMail = data.find((tpl) => tpl.tipoMail === 'EnvioMail');
            setMailAsunto(envioMail?.asunto || '');
            setMailHtmlTemplate(envioMail?.htmlTemplate || '');
            setMailEsDefault(!!envioMail?.esDefault);
            setMailDialogVisible(true);
        } catch (err) {
            console.error('Error al cargar la plantilla de mail:', err);
            alert('No se pudo cargar la plantilla de mail.');
        } finally {
            setCargandoPlantillaMail(false);
        }
    };

    const handleConfirmarEnvioMail = async () => {
        setEnviandoMail(true);
        try {
            const adjuntoBase64 = await generarPdfBase64(pdfRef.current);

            const [personasRes] = await Promise.all([getPersonasBarrio(barrio.id, administracionId)]);
            const personas = personasRes.data.filter(p => p.email);

            if (personas.length === 0) {
                alert('No hay destinatarios con email registrado.');
                return;
            }

            const periodoStr = selectedDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
            const barrioNombre = barrio?.nombre || `el ${t.barrio}`;

            const { data } = await enviarConTemplate({
                administracionId,
                barrioId: barrio.id,
                tipoMail: 'EnvioMail',
                asunto: mailAsunto,
                htmlTemplate: mailHtmlTemplate,
                placeholdersComunes: { barrio: barrioNombre, periodo: periodoStr },
                destinatarios: personas.map((persona) => ({
                    email: persona.email,
                    placeholders: { nombrePersona: persona.nombreCompleto || 'propietario/a' },
                })),
                adjuntoBase64,
                adjuntoNombre: 'liquidacion.pdf',
            });

            setMailDialogVisible(false);
            showSuccess('Mail enviado', `Enviado correctamente a ${data.enviados} destinatario(s).`);
        } catch (err) {
            console.error('Error al enviar mail:', err);
            alert('Error al enviar el mail: ' + err.message);
        } finally {
            setEnviandoMail(false);
        }
    };

    // ── Estados para los gráficos de gastos ──
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();
    const [resumen, setResumen] = useState(null);
    const [gastosPorRubro, setGastosPorRubro] = useState([]);
    const [gastosPorProveedor, setGastosPorProveedor] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [comunicadosData, setComunicadosData] = useState([]);
    const [reservasDash,    setReservasDash]    = useState([]);
    const [reclamosDash,    setReclamosDash]    = useState([]);

    const { sinAcceso } = usePersonaResidente();
    const rolId     = parseInt(localStorage.getItem('rolId') || '1');
    const esAdmin   = rolId === 1 || rolId === ROL_PERFIL_ADMINISTRADOR;
    const _dashUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
    const usuarioId = _dashUser.id || _dashUser.usuarioId || 0;

    const addHourDash = (t) => {
        if (!t) return '01:00';
        const [h, m] = t.split(':').map(Number);
        return `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    const eventosDash = useMemo(() => reservasDash.map(r => {
        const fecha     = r.fecha?.substring(0, 10) ?? '';
        const todoElDia = !r.horaInicio && !r.horaFin;
        const color     = r.estado === 'Cancelada' ? '#f04438' : '#1570ef';
        return {
            id:              String(r.reservaId),
            title:           r.salonNombre,
            start:           todoElDia ? fecha : `${fecha}T${r.horaInicio || '00:00'}`,
            end:             todoElDia ? fecha : `${fecha}T${r.horaFin || addHourDash(r.horaInicio)}`,
            allDay:          todoElDia,
            backgroundColor: color,
            borderColor:     color,
            extendedProps:   { reserva: r },
        };
    }), [reservasDash]);

    const fetchComunicados = useCallback(async () => {
        if (!barrio?.id || esAdmin) return;
        try {
            const { data } = await axios.get(
                `${HOST_API}api/comunicados/GetComunicados?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setComunicadosData(data);
        } catch { /* silencioso en dashboard */ }
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => { fetchComunicados(); }, [fetchComunicados]);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/reservasSUM/GetReservas?administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setReservasDash(data))
            .catch(() => {});
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => {
        if (!barrio?.id || esAdmin || !usuarioId) return;
        axios.get(`${HOST_API}api/reclamos/GetReclamosUsuario?administracionId=${administracionId}&barrioId=${barrio.id}&usuarioId=${usuarioId}`)
            .then(({ data }) => setReclamosDash(data))
            .catch(() => {});
    }, [barrio?.id, rolId, usuarioId, HOST_API, administracionId]);

    // ── Saldo Expensas (Portal Barrio): UFs del usuario + vencimiento del periodo ──
    const [ufsUsuario, setUfsUsuario] = useState([]);
    const [ufSeleccionada, setUfSeleccionada] = useState(null);
    const [infoExpensaDash, setInfoExpensaDash] = useState(null);

    useEffect(() => {
        if (!barrio?.id || esAdmin || !usuarioId) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/GetUFsByUsuario?usuarioId=${usuarioId}&barrioId=${barrio.id}`)
            .then(({ data }) => {
                setUfsUsuario(data ?? []);
                setUfSeleccionada((prev) => prev ?? (data?.[0]?.unidadFuncionalId ?? null));
            })
            .catch(() => {});
    }, [barrio?.id, rolId, usuarioId, HOST_API]);

    useEffect(() => {
        if (!barrio?.id || esAdmin || !ufSeleccionada) { setInfoExpensaDash(null); return; }
        const d = selectedDate || new Date();
        axios.get(`${HOST_API}api/pagosUF/GetInformarPagosBarrio`, {
            params: { unidadFuncionalId: ufSeleccionada, barrioId: barrio.id, administracionId, conceptoPagoId: 1, periodo: d.toISOString() }
        })
            .then(({ data }) => setInfoExpensaDash(data))
            .catch(() => setInfoExpensaDash(null));
    }, [barrio?.id, rolId, ufSeleccionada, administracionId, selectedDate, HOST_API]);

    const handleDateChange = (value) => {
      setSelectedDate(value);
    };

    // ── Fetch dashboard de gastos ──
    useEffect(() => {
        if (!barrio?.id) return;

        const barrioId = barrio.id;
        const d = selectedDate || new Date();
        const periodo = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        const fetchDashboard = async () => {
            setLoading(true);
            setError(null);
            try {
                const [resumenRes, rubrosRes, proveedoresRes] = await Promise.all([
                    getResumenGeneral(barrioId, administracionId, periodo),
                    getGastosPorRubro(barrioId, administracionId, periodo),
                    getGastosPorProveedor(barrioId, administracionId, periodo),
                ]);
                setResumen(resumenRes.data);
                setGastosPorRubro(rubrosRes.data);
                setGastosPorProveedor(proveedoresRes.data);
            } catch (err) {
                setError(err.response?.data?.errorDisplay || "Error al cargar el dashboard");
            } finally {
                setLoading(false);
            }
        };

        fetchDashboard();
    }, [selectedDate, barrio?.id, administracionId]);

    // ── Fetch customers (tabla existente) ──
    useEffect(() => {
        const customerService = new CustomerService();

        customerService.getCustomersLarge().then((customers) => {
            setCustomersTable(customers.map((c) => ({ ...c, date: new Date(c.date) })));
        });
        customerService.getCustomersLarge().then((customers) => {
            setCustomersTable1(customers.map((c) => ({ ...c, date: new Date(c.date) })));
        });
        customerService.getCustomersMixed().then((customers) => {
            setCustomersTable2(customers.map((c) => ({ ...c, date: new Date(c.date) })));
        });

        setCustomerCarousel([
            { user: '9,673 Users', value: '$8,362,478', image: 'nasa' },
            { user: '9,395 Users', value: '$7,927,105', image: 'beats' },
            { user: '7,813 Users', value: '$6,471,594', image: 'gopro' },
            { user: '7,613 Users', value: '$5,697,883', image: 'north' },
            { user: '98,673 Users', value: '$7,653,311', image: 'mc' },
            { user: '5,645 Users', value: '$4,567,823', image: 'dell' },
            { user: '5,153 Users', value: '$5,342,678', image: 'wwf' },
            { user: '4,338 Users', value: '$5,867,391', image: 'bmw' },
            { user: '4,170 Users', value: '$4,647,233', image: 'pepsi' },
            { user: '3,765 Users', value: '$4,123,876', image: 'netflix' },
            { user: '3,490 Users', value: '$3,688,362', image: 'deloitte' },
            { user: '2,976 Users', value: '$3,978,478', image: 'pg' }
        ]);
    }, []);

    // ── Helpers de formato ──
    const formatARS = (value) =>
        new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(value);

    // ── Datos para Doughnut (gastos por rubro) ──
    const chartRubros = useMemo(() => ({
        labels: gastosPorRubro.map(g => g.grupoGasto),
        datasets: [{
            data: gastosPorRubro.map(g => g.totalRubro),
            backgroundColor: CHART_COLORS.slice(0, gastosPorRubro.length),
            borderWidth: 2,
        }]
    }), [gastosPorRubro]);

    const optionsRubros = useMemo(() => {
        const totalGastos = gastosPorRubro.reduce((sum, g) => sum + g.totalRubro, 0);
        return {
            cutout: '65%',
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        boxWidth: 12,
                        padding: 14,
                        font: { size: 11 },
                        generateLabels: (chart) =>
                            chart.data.labels.map((label, i) => ({
                                text: label.length > 28 ? label.slice(0, 28) + '…' : label,
                                fillStyle: CHART_COLORS[i % CHART_COLORS.length],
                                strokeStyle: CHART_COLORS[i % CHART_COLORS.length],
                                hidden: false,
                                index: i,
                            })),
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (ctx) => {
                            const item = gastosPorRubro[ctx.dataIndex];
                            return [
                                ` ${formatARS(item.totalRubro)}`,
                                ` ${item.porcentaje.toFixed(1)}%`,
                                ` ${item.cantidadGastos} gasto(s)`,
                            ];
                        }
                    }
                },
                datalabels: {
                    display: (ctx) => {
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        return total > 0 && (ctx.dataset.data[ctx.dataIndex] / total) * 100 >= 5;
                    },
                    color: '#fff',
                    anchor: 'center',
                    align: 'center',
                    font: { weight: 'bold', size: 12 },
                    formatter: (value, ctx) => {
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        return `${((value / total) * 100).toFixed(1)}%`;
                    },
                    textShadowColor: 'rgba(0,0,0,0.35)',
                    textShadowBlur: 4,
                },
                centerText: {
                    display: true,
                    value: formatARS(totalGastos),
                },
            }
        };
    }, [gastosPorRubro]);

    // ── Datos para Bar horizontal (gastos por proveedor) ──
    const proveedoresOrdenados = useMemo(() =>
        [...gastosPorProveedor].sort((a, b) => b.totalProveedor - a.totalProveedor),
    [gastosPorProveedor]);

    const chartProveedores = useMemo(() => ({
        labels: proveedoresOrdenados.map(p => p.nombreProveedor),
        datasets: [{
            label: 'Total',
            data: proveedoresOrdenados.map(p => p.totalProveedor),
            backgroundColor: proveedoresOrdenados.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]), // 👈
            borderRadius: 4,
        }]
    }), [proveedoresOrdenados]);

    const optionsProveedores = useMemo(() => ({
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const item = proveedoresOrdenados[ctx.dataIndex];
                        return [
                            ` ${formatARS(item.totalProveedor)}`,
                            ` ${item.cantidadGastos} gasto(s)`,
                        ];
                    }
                }
            },
            datalabels: { display: false }
        },
        scales: {
            x: {
                ticks: { callback: (val) => `$${(val / 1000).toFixed(0)}k` },
                grid: { color: '#e5e7eb' }
            },
            y: {
                ticks: { font: { size: 11 } },
                grid: { display: false }
            }
        }
    }), [proveedoresOrdenados]);

    // ── Handlers de charts existentes ──
    const changeRevenueChart = (event) => {
        setSelectedRevenueMonth(event.value);
        const dataSet1 = [
            [37, 34, 21, 27, 10, 18, 15],
            [31, 27, 30, 37, 23, 29, 20],
            [21, 7, 13, 3, 19, 11, 6],
            [47, 31, 35, 20, 46, 39, 25]
        ];
        const dataSet2 = [
            [31, 27, 30, 37, 23, 29, 20],
            [47, 31, 35, 20, 46, 39, 25],
            [37, 34, 21, 27, 10, 18, 15],
            [21, 7, 13, 3, 19, 11, 6]
        ];
        if (event.value.code === '1') {
            revenueChart.datasets[0].data = dataSet2[0];
            revenueChart.datasets[1].data = dataSet2[1];
            revenueChart.datasets[2].data = dataSet2[2];
            revenueChart.datasets[3].data = dataSet2[3];
        } else {
            revenueChart.datasets[0].data = dataSet1[0];
            revenueChart.datasets[1].data = dataSet1[1];
            revenueChart.datasets[2].data = dataSet1[2];
            revenueChart.datasets[3].data = dataSet1[3];
        }
        revenue.current.refresh();
    };

    const changeVisitorChart = (event) => {
        setSelectedVisitorYear(event.value);
        const dataSet1 = [
            [630, 630, 695, 695, 695, 760, 760, 760, 840, 840, 840, 840],
            [600, 671, 660, 665, 700, 610, 810, 790, 710, 860, 810, 780]
        ];
        const dataSet2 = [
            [580, 580, 620, 620, 620, 680, 680, 680, 730, 730, 730, 730],
            [550, 592, 600, 605, 630, 649, 660, 690, 710, 720, 730, 780]
        ];
        if (event.value.code === '1') {
            growth = '$581,259';
            avgCustomer = '$973';
            visitorChart.datasets[0].data = dataSet2[0];
            visitorChart.datasets[1].data = dataSet2[1];
        } else {
            growth = '$620,076';
            avgCustomer = '$1,120';
            visitorChart.datasets[0].data = dataSet1[0];
            visitorChart.datasets[1].data = dataSet1[1];
        }
        visitor.current.refresh();
    };

    const changeCustomerChart = (event) => {
        setSelectedCustomerYear(event.value);
        const dataSet1 = [
            [10,25,48,35,54,70],[18,35,23,30,59,65],[20,47,46,46,61,70],[17,34,18,48,67,68],
            [9,37,47,50,60,62],[8,48,40,52,72,75],[10,18,50,47,63,80],[20,36,39,58,59,85],
            [30,45,35,50,54,81],[28,35,52,56,60,77],[40,40,38,45,68,86],[50,23,27,34,65,90],
            [29,27,29,42,55,84],[10,37,47,29,59,80],[10,54,42,38,63,83],[25,44,50,56,65,92],
            [30,43,48,45,73,78],[29,47,54,60,77,86]
        ];
        const dataSet2 = [
            [10,25,48,35,54,70],[20,47,46,46,61,70],[17,34,18,48,67,68],[50,23,27,34,65,90],
            [8,48,40,52,72,75],[9,37,47,50,60,62],[10,18,50,47,63,80],[30,45,35,50,54,81],
            [10,37,47,29,59,80],[28,35,52,56,60,77],[25,44,50,56,65,92],[18,35,23,30,59,65],
            [20,36,39,58,59,85],[29,27,29,42,55,84],[40,40,38,45,68,86],[30,43,48,45,73,78],
            [10,54,42,38,63,83],[29,47,54,60,77,86]
        ];

        const ds = event.value.code === '1' ? dataSet2 : dataSet1;
        if (event.value.code === '1') {
            customerAvg = '621'; customerMin = '198'; customerMax = '957';
        } else {
            customerAvg = '875'; customerMin = '284'; customerMax = '1232';
        }
        ds.forEach((d, i) => { customerChart.datasets[i].data = d; });
        customer.current.refresh();
    };

    const recentSales = (event) => {
        setSelectedOrderYear(event.value);
        setCustomersTable(event.value.code === '0' ? customersTable1 : customersTable2);
    };

    const formatDate = (value) => (
        <>
            <span className="p-column-title">Date</span>
            {value.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        </>
    );

    const formatCurrency = (value) => (
        <>
            <span className="p-column-title">Balance</span>
            {value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        </>
    );

    const representativeTemplate = (data) => (
        <>
            <span className="p-column-title">Agent</span>
            <img alt={data.representative.name} src={'assets/demo/images/avatar/' + data.representative.image} width={24} className="mr-2" style={{ verticalAlign: 'middle' }} />
            <span className="image-text">{data.representative.name}</span>
        </>
    );

    const actionTemplate = () => (
        <>
            <Button className="p-button-text" icon="pi pi-copy"></Button>
            <Button className="p-button-text" icon="pi pi-pencil"></Button>
            <Button className="p-button-text" icon="pi pi-ellipsis-h"></Button>
        </>
    );

    const countryTemplate = (data, props) => (
        <>
            <span className="p-column-title">{props.header}</span>
            <span className="ml-2" style={{ verticalAlign: 'middle' }}>{data.country.name}</span>
        </>
    );

    const itemTemplate = (customer) => (
        <div className="card mr-4">
            <div className="customer-item-content">
                <div className="mb-6">
                    <img src={'assets/layout/images/dashboard/' + customer.image + '.png'} alt={customer.image} className="product-image" />
                </div>
                <div>
                    <h4>{customer.user}</h4>
                    <h5 className="mt-0 mb-3">{customer.value}</h5>
                </div>
            </div>
        </div>
    );

    const marker = (item) => (
        <span className="custom-marker" style={{ backgroundColor: item.iconColor }}>
            <i className={item.icon}></i>
        </span>
    );

    const content = (item) => (
        <>
            <div className="flex align-items-center justify-content-between">
                <p>{item.transaction}</p>
                <h6 style={{ color: item.amountColor }}>{item.amount}</h6>
            </div>
            <span>{item.date}</span>
        </>
    );

    if (sinAcceso) return <SinAccesoResidente />;

    return (
        <div className="layout-dashboard">
            <h4>Dashboard</h4>
            <br />

            <Toast ref={toast} />

            <LoadingOverlay visible={loading} />

            {/*
                Wrapper off-screen: mantiene el contenido fuera de la vista.
                pdfRef apunta al div INTERNO (sin posicionamiento) para que
                react-to-print copie el contenido limpio al iframe de impresión.
            */}
            <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '210mm' }}>
                <div ref={pdfRef}>
                    <ReportePDF
                        barrio={barrio}
                        periodo={selectedDate}
                        pdfData={pdfData}
                        vencimiento={pdfData?.expensas?.[0]?.fechaVencimiento}
                    />
                </div>
            </div>

            {/* Dialog solo para preview visual */}
            <Dialog
                header="Preview Liquidación de Expensas"
                visible={pdfDialogVisible}
                style={{ width: '90vw', maxWidth: '900px' }}
                onHide={() => setPdfDialogVisible(false)}
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button
                            label="Imprimir / Guardar PDF"
                            icon="pi pi-print"
                            onClick={handlePrint}
                        />
                        <Button
                            label="Descargar Comprobantes"
                            icon="pi pi-download"
                            loading={descargandoComprobantes}
                            onClick={handleDescargarComprobantes}
                        />
                        <Button
                            label="Enviar por mail"
                            icon="pi pi-envelope"
                            loading={cargandoPlantillaMail}
                            onClick={handleAbrirDialogMail}
                        />
                        <Button
                            label="Cerrar"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => setPdfDialogVisible(false)}
                        />
                    </div>
                }
            >
                <div style={{ overflowY: 'auto', maxHeight: '75vh' }}>
                    <ReportePDF
                        barrio={barrio}
                        periodo={selectedDate}
                        pdfData={pdfData}
                        vencimiento={pdfData?.expensas?.[0]?.fechaVencimiento}
                    />
                </div>
            </Dialog>

            {/* Popup para revisar/editar el mail justo antes de enviarlo */}
            <Dialog
                header="Enviar por mail"
                visible={mailDialogVisible}
                style={{ width: '90vw', maxWidth: '750px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setMailDialogVisible(false)}
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => setMailDialogVisible(false)}
                        />
                        <Button
                            label="Enviar correo"
                            icon="pi pi-send"
                            loading={enviandoMail}
                            onClick={handleConfirmarEnvioMail}
                        />
                    </div>
                }
            >
                <MailTemplateEditor
                    tipoMail="EnvioMail"
                    asunto={mailAsunto}
                    onChangeAsunto={setMailAsunto}
                    htmlTemplate={mailHtmlTemplate}
                    onChangeHtml={setMailHtmlTemplate}
                    barrioNombre={barrio?.nombre}
                    esDefault={mailEsDefault}
                    onError={(msg) => toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 })}
                />
            </Dialog>

            {/* Dialog Medios de pago (Portal Barrio) */}
            <Dialog
                header="Medios de pago"
                visible={mediosPagoVisible}
                style={{ width: '90vw', maxWidth: '420px' }}
                onHide={() => setMediosPagoVisible(false)}
            >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {[
                        { label: `${barrio.cuentaBancaria || 'Banco'} - ${barrio.tipoCuentaBancaria || 'CBU'}`, valor: barrio.numeroCuenta },
                        { label: `${barrio.cuentaBancaria || 'Banco'} - Alias`, valor: barrio.alias },
                    ].filter((row) => row.valor).map((row, idx) => (
                        <div key={idx} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '0.9rem 0', borderBottom: '1px solid #eaecf0',
                        }}>
                            <div>
                                <div style={{ fontWeight: 600, color: '#101828', fontSize: '0.9rem' }}>{row.label}</div>
                                <div style={{ color: '#667085', fontSize: '0.85rem', marginTop: '0.15rem' }}>{row.valor}</div>
                            </div>
                            <Button
                                icon="pi pi-copy"
                                className="p-button-text p-button-rounded"
                                onClick={() => copiarAlPortapapeles(row.valor, row.label)}
                            />
                        </div>
                    ))}
                    {!barrio.numeroCuenta && !barrio.alias && (
                        <p style={{ color: '#98a2b3', fontSize: '0.85rem', textAlign: 'center', margin: '1rem 0' }}>
                            Todavía no se cargaron los datos bancarios del barrio.
                        </p>
                    )}
                </div>
            </Dialog>

            <div className="flex align-items-center gap-3">

                <Calendar
                    value={selectedDate}
                    view="month"
                    dateFormat="mm/yy"
                    onChange={(e) => handleDateChange(e.value)}
                    showIcon
                    className="p-inputtext-sm"
                />

                {esAdmin && (
                    <Button
                        label="Generar PDF"
                        icon="pi pi-file-pdf"
                        className="p-button-success"
                        onClick={() => descargarPdf()}
                    />
                )}
            </div>

            {/* ── Portal Barrio: card de saldo (solo usuarios de barrio) ── */}
            {!esAdmin && (() => {
                const periodoLabel = selectedDate
                    ? selectedDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })
                    : '';
                const saldo = infoExpensaDash?.importePrimerVencimiento ?? 0;
                const alDia = saldo <= 0;
                const formatMoney = (n) =>
                    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n ?? 0);

                const btnIconStyle = {
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: '0.5rem', cursor: 'pointer', background: 'none', border: 'none'
                };
                const circleStyle = {
                    width: 48, height: 48, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.2rem', color: '#344054', background: '#f2f4f7'
                };
                const cardStyle = {
                    background: '#fff', border: '1px solid #e4e7ec',
                    borderRadius: 16, padding: '1.5rem 1.5rem 1.25rem',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', flex: '1 1 340px', minWidth: 280
                };

                const fmtFecha = (f) => {
                    if (!f) return '';
                    const d = new Date(f);
                    const hoy = new Date(); const ayer = new Date(hoy); ayer.setDate(hoy.getDate() - 1);
                    if (d.toDateString() === hoy.toDateString()) return 'Hoy';
                    if (d.toDateString() === ayer.toDateString()) return 'Ayer';
                    return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
                };

                return (
                    <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '1.5rem', marginTop: '1.2rem' }}>

                        {/* ── Card Saldo Expensas (estética app móvil) ── */}
                        <div style={{ flex: '1 1 340px', minWidth: 280 }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #4f8ff7, #0a3d91)',
                                borderRadius: 24,
                                padding: '1.25rem 1.25rem 2.5rem',
                                boxShadow: '0 14px 30px -12px rgba(10,61,145,0.4)',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
                                        Saldo Expensas
                                    </span>
                                    <span style={{
                                        background: 'rgba(255,255,255,0.18)', borderRadius: 99,
                                        padding: '0.25rem 0.65rem', fontSize: '0.72rem', fontWeight: 700, color: '#fff'
                                    }}>
                                        {alDia ? '✓ Al día' : '⚠ Pendiente'}
                                    </span>
                                </div>
                                {ufsUsuario.length > 1 && (
                                    <Dropdown
                                        value={ufSeleccionada}
                                        options={ufsUsuario}
                                        optionLabel="unidadFuncional"
                                        optionValue="unidadFuncionalId"
                                        onChange={(e) => setUfSeleccionada(e.value)}
                                        className="p-inputtext-sm"
                                        style={{ marginTop: '0.6rem', width: '100%' }}
                                    />
                                )}
                                <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)', marginTop: '0.6rem', marginBottom: 0, textTransform: 'capitalize' }}>
                                    {periodoLabel}
                                </p>
                                <p style={{ fontSize: '2rem', fontWeight: 800, color: '#fff', margin: '0.2rem 0 0' }}>
                                    {formatMoney(saldo)}
                                </p>
                            </div>
                            <div style={{
                                display: 'flex', justifyContent: 'space-around',
                                background: '#fff', borderRadius: 20,
                                marginTop: -24, marginLeft: 16, marginRight: 16,
                                paddingTop: '1rem', paddingBottom: '0.5rem',
                                boxShadow: '0 6px 16px -8px rgba(16,24,40,0.15)',
                            }}>
                                <button style={btnIconStyle} onClick={() => navigate('/subirComprobante')}>
                                    <div style={circleStyle}><i className="pi pi-credit-card" /></div>
                                    <span style={{ fontSize: '0.72rem', color: '#344054', textAlign: 'center' }}>Informar<br/>pago</span>
                                </button>
                                <button style={btnIconStyle} onClick={() => navigate('/estadoCuenta')}>
                                    <div style={circleStyle}><i className="pi pi-file" /></div>
                                    <span style={{ fontSize: '0.72rem', color: '#344054', textAlign: 'center' }}>Estado<br/>de cuenta</span>
                                </button>
                                <button style={btnIconStyle} onClick={() => setMediosPagoVisible(true)}>
                                    <div style={circleStyle}><i className="pi pi-wallet" /></div>
                                    <span style={{ fontSize: '0.72rem', color: '#344054', textAlign: 'center' }}>Medios<br/>de pago</span>
                                </button>
                                <button style={btnIconStyle} onClick={() => navigate('/gastosConsorcio')}>
                                    <div style={circleStyle}><i className="pi pi-chart-pie" /></div>
                                    <span style={{ fontSize: '0.72rem', color: '#344054', textAlign: 'center' }}>Gastos<br/>consorcio</span>
                                </button>
                            </div>
                        </div>

                        {/* ── Card Últimos comunicados ── */}
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <span style={{ fontWeight: 600, color: '#101828', fontSize: '0.95rem' }}>Últimos comunicados</span>
                                <button onClick={() => navigate('/comunicadosBarrio')} style={{ background: 'none', border: 'none', color: '#1570ef', fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}>
                                    Ver todos →
                                </button>
                            </div>
                            {comunicadosData.length === 0 ? (
                                <p style={{ color: '#98a2b3', fontSize: '0.85rem', textAlign: 'center', margin: '1rem 0' }}>Sin comunicados recientes</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                    {comunicadosData.slice(0, 4).map((c) => (
                                        <div key={c.comunicadoId} style={{
                                            background:   '#fff',
                                            border:       `1px solid ${c.urgente ? '#fecdca' : '#e4e7ec'}`,
                                            borderLeft:   `4px solid ${c.urgente ? '#f04438' : '#1570ef'}`,
                                            borderRadius: 10,
                                            padding:      '0.75rem 0.9rem',
                                            boxShadow:    '0 1px 3px rgba(0,0,0,0.04)',
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.3rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: 1, minWidth: 0 }}>
                                                    {c.urgente && (
                                                        <span style={{
                                                            background: '#fef3f2', color: '#f04438', fontWeight: 700,
                                                            fontSize: '0.65rem', padding: '0.1rem 0.4rem', borderRadius: 99,
                                                            border: '1px solid #fecdca', whiteSpace: 'nowrap', flexShrink: 0,
                                                        }}>
                                                            URGENTE
                                                        </span>
                                                    )}
                                                    <span style={{ fontWeight: 600, color: '#101828', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {c.asunto}
                                                    </span>
                                                </div>
                                                <span style={{ fontSize: '0.75rem', color: '#98a2b3', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                    {fmtFecha(c.fecha)}
                                                </span>
                                            </div>
                                            <p style={{ margin: 0, fontSize: '0.8rem', color: '#667085', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                {c.comunicado}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* ── Card Últimos reclamos ── */}
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <span style={{ fontWeight: 600, color: '#101828', fontSize: '0.95rem' }}>Últimos reclamos</span>
                                <button onClick={() => navigate('/reclamos')} style={{ background: 'none', border: 'none', color: '#1570ef', fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}>
                                    Ver todos →
                                </button>
                            </div>
                            {reclamosDash.length === 0 ? (
                                <p style={{ color: '#98a2b3', fontSize: '0.85rem', textAlign: 'center', margin: '1rem 0' }}>Sin reclamos recientes</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                    {reclamosDash.slice(0, 4).map((r) => {
                                        const leido = r.estado === 'leido' || r.estado === 'Leído';
                                        return (
                                            <div key={r.reclamoId} style={{
                                                background:   '#fff',
                                                border:       `1px solid ${leido ? '#e4e7ec' : '#fef0c7'}`,
                                                borderLeft:   `4px solid ${leido ? '#12b76a' : '#f79009'}`,
                                                borderRadius: 10,
                                                padding:      '0.75rem 0.9rem',
                                                boxShadow:    '0 1px 3px rgba(0,0,0,0.04)',
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.3rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0 }}>
                                                        <span style={{
                                                            background: leido ? '#ecfdf3' : '#fffaeb',
                                                            color:      leido ? '#12b76a' : '#b54708',
                                                            border:     `1px solid ${leido ? '#a9efc5' : '#fedf89'}`,
                                                            fontWeight: 700, fontSize: '0.65rem',
                                                            padding: '0.1rem 0.4rem', borderRadius: 99,
                                                            whiteSpace: 'nowrap', flexShrink: 0,
                                                        }}>
                                                            {leido ? 'LEÍDO' : 'PENDIENTE'}
                                                        </span>
                                                        <span style={{ fontWeight: 600, color: '#101828', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {r.tipoReclamoNombre || r.tipoNombre || 'Reclamo'}
                                                        </span>
                                                    </div>
                                                    <span style={{ fontSize: '0.75rem', color: '#98a2b3', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                        {fmtFecha(r.fecha)}
                                                    </span>
                                                </div>
                                                {r.descripcion && (
                                                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#667085', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                        {r.descripcion}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                    </div>
                );
            })()}

            <br/>

            <div className="grid">

                {/* ── KPI Cards de gastos ── */}
                {resumen && (
                    <div className="col-12">
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="card">
                                    <span className="block text-500 font-medium mb-2">Total general de gastos</span>
                                    <div className="text-900 font-semibold text-3xl">
                                        {formatARS(resumen.totalGeneral)}
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 md:col-6">
                                <div className="card">
                                    <span className="block text-500 font-medium mb-2">Cantidad de gastos</span>
                                    <div className="text-900 font-semibold text-3xl">
                                        {resumen.cantidadGastos}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Doughnut: gastos por grupo + Bar: gastos por proveedor ── */}
                {(gastosPorRubro.length > 0 || gastosPorProveedor.length > 0) && (
                    <>
                        {gastosPorRubro.length > 0 && (
                            <div className="col-12 md:col-4">
                                <div className="card" style={{ height: '100%' }}>
                                    <h5 className="mt-0">Gastos por grupo</h5>
                                    <Chart
                                        ref={chartRubrosRef}
                                        type="doughnut"
                                        data={chartRubros}
                                        options={optionsRubros}
                                        style={{ height: '384px' }}
                                    />
                                </div>
                            </div>
                        )}
                        {gastosPorProveedor.length > 0 && (
                            <div className="col-12 md:col-8">
                                <div className="card" style={{ height: '100%', overflow: 'hidden' }}>
                                    <h5 className="mt-0">Gastos por proveedor</h5>
                                    <Chart
                                        type="bar"
                                        data={chartProveedores}
                                        options={optionsProveedores}
                                        style={{ height: `${Math.max(260, proveedoresOrdenados.length * 45)}px` }}
                                    />
                                </div>
                            </div>
                        )}

                        {gastosPorProveedor.length > 0 && (
                            <div className="col-12">
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                    {proveedoresOrdenados.map((p, idx) => (
                                        <div key={p.proveedorId} style={{ flex: '1 1 180px', minWidth: 180, maxWidth: 260 }}>
                                            <SummaryStatsCard
                                                icon="pi pi-truck"
                                                title={p.nombreProveedor}
                                                theme={SUMMARY_STATS_THEME_CYCLE[idx % SUMMARY_STATS_THEME_CYCLE.length]}
                                                items={[
                                                    { label: 'Total', value: formatARS(p.totalProveedor) },
                                                    { label: p.cantidadGastos !== 1 ? 'Gastos' : 'Gasto', value: p.cantidadGastos },
                                                ]}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {error && (
                    <div className="col-12">
                        <div className="card">
                            <span className="text-red-500">{error}</span>
                        </div>
                    </div>
                )}

{/* 
                <div className="col-12 md:col-4">
                    <div className="card widget-overview-box widget-overview-box-1">
                        <span className="overview-title">Gastos totales</span>
                        <div className="flex justify-content-between">
                            <div className="overview-detail flex justify-content-between">
                                <div className="overview-badge flex justify-content-center align-items-center">
                                    <i className="pi pi-arrow-down"></i>
                                    <span>0.6%</span>
                                </div>
                                <div className="overview-text">0.81%</div>
                            </div>
                        </div>
                        <img src="assets/layout/images/dashboard/rate.svg" alt="rate" />
                    </div>
                </div>
                <div className="col-12 md:col-4">
                    <div className="card widget-overview-box widget-overview-box-2">
                        <span className="overview-title">Ganancia</span>
                        <div className="flex justify-content-between">
                            <div className="overview-detail flex justify-content-between">
                                <div className="overview-badge flex justify-content-center align-items-center">
                                    <i className="pi pi-arrow-up"></i>
                                    <span>4,2%</span>
                                </div>
                                <div className="overview-text">$306.2</div>
                            </div>
                        </div>
                        <img src="assets/layout/images/dashboard/value.svg" alt="value" />
                    </div>
                </div>
                <div className="col-12 md:col-4">
                    <div className="card widget-overview-box widget-overview-box-3">
                        <span className="overview-title">Cantidad de barrios</span>
                        <div className="flex justify-content-between">
                            <div className="overview-detail flex justify-content-between">
                                <div className="overview-badge flex justify-content-center align-items-center">
                                    <i className="pi pi-minus"></i>
                                    <span>2,1%</span>
                                </div>
                                <div className="overview-text">1,620</div>
                            </div>
                        </div>
                        <img src="assets/layout/images/dashboard/quantity.svg" alt="quantity" />
                    </div>
                </div>

                <div className="col-12 md:col-8">
                    <div className="card widget-visitor-graph">
                        <div className="card-header">
                            <span>Unique visitor graph</span>
                            <Dropdown options={visitorYear} value={selectedVisitorYear} optionLabel="name" onChange={changeVisitorChart}></Dropdown>
                        </div>
                        <div className="graph-content grid">
                            <div className="col-12 md:col-6">
                                <h2>{growth}</h2>
                                <h6>MRR GROWTH</h6>
                                <p>Measure how fast you're growing mothly recurring revenue. <button className="p-link">Learn more</button></p>
                            </div>
                            <div className="col-12 md:col-6">
                                <h2>{avgCustomer}</h2>
                                <h6>AVG. MRR/CUSTOMER</h6>
                                <p>The revenue generated per account on a monthly or yearly basis. <button className="p-link">Learn more</button></p>
                            </div>
                        </div>
                        <div className="graph">
                            <h6>Revenue</h6>
                            <Chart ref={visitor} type="bar" data={visitorChart} options={visitorChartOptions} id="visitor-chart"></Chart>
                        </div>
                    </div>
                </div>

                <div className="col-12 md:col-4">
                    <div className="card widget-timeline">
                        <div className="timeline-header flex justify-content-between align-items-center">
                            <p>Transaction history</p>
                            <div className="header-icons">
                                <i className="pi pi-refresh"></i>
                                <i className="pi pi-filter"></i>
                            </div>
                        </div>
                        <div className="timeline-content">
                            <Timeline value={timelineEvents} marker={marker} content={content} className="custimized-timeline" />
                        </div>
                        <div className="timeline-footer flex align-items-center justify-content-center">
                            <button className="p-link">View all transactions <i className="pi pi-arrow-down"></i></button>
                        </div>
                    </div>
                </div>

                <div className="col-12 md:col-4">
                    <div className="card widget-country-graph">
                        <div className="country-title">Country distrubutions</div>
                        <div className="country-graph flex justify-content-center">
                            <Chart type="doughnut" id="country-chart" data={countryChart} options={countryChartOptions} style={{ position: 'relative', width: '75%' }}></Chart>
                        </div>
                        <div className="country-content">
                            <ul>
                                <li className="flex justify-content-between align-items-center">
                                    <div className="flex justify-content-between align-items-center">
                                        <div className="color" style={{ backgroundColor: '#00D0DE', boxShadow: '0px 0px 10px rgba(0, 208, 222, 0.3)' }}></div>
                                        <span>United States of America</span>
                                    </div>
                                    <span>25%</span>
                                </li>
                                <li className="flex justify-content-between align-items-center">
                                    <div className="flex justify-content-between align-items-center">
                                        <div className="color" style={{ backgroundColor: '#873EFE', boxShadow: '0px 0px 10px rgba(135, 62, 254, 0.3)' }}></div>
                                        <span>China</span>
                                    </div>
                                    <span>20%</span>
                                </li>
                                <li className="flex justify-content-between align-items-center">
                                    <div className="flex justify-content-between align-items-center">
                                        <div className="color" style={{ backgroundColor: '#FC6161', boxShadow: '0px 0px 10px rgba(252, 97, 97, 0.3)' }}></div>
                                        <span>Japan</span>
                                    </div>
                                    <span>17%</span>
                                </li>
                                <li className="flex justify-content-between align-items-center">
                                    <div className="flex justify-content-between align-items-center">
                                        <div className="color" style={{ backgroundColor: '#EEE500', boxShadow: '0px 0px 10px rgba(238, 229, 0, 0.3)' }}></div>
                                        <span>Australia</span>
                                    </div>
                                    <span>15%</span>
                                </li>
                                <li className="flex justify-content-between align-items-center">
                                    <div className="flex justify-content-between align-items-center">
                                        <div className="color" style={{ backgroundColor: '#EC4DBC', boxShadow: '0px 0px 10px rgba(236, 77, 188, 0.3)' }}></div>
                                        <span>India</span>
                                    </div>
                                    <span>10%</span>
                                </li>
                                <li className="flex justify-content-between align-items-center">
                                    <div className="flex justify-content-between align-items-center">
                                        <div className="color" style={{ backgroundColor: '#0F8BFD', boxShadow: '0px 0px 10px rgba(15, 139, 253, 0.3)' }}></div>
                                        <span>Rusian Federation</span>
                                    </div>
                                    <span>8%</span>
                                </li>
                                <li className="flex justify-content-between align-items-center">
                                    <div className="flex justify-content-between align-items-center">
                                        <div className="color" style={{ backgroundColor: '#545C6B' }}></div>
                                        <span>Others</span>
                                    </div>
                                    <span>5%</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="col-12 md:col-8">
                    <div className="card widget-revenue-graph">
                        <div className="card-header">
                            <span>Monthly revenue</span>
                            <Dropdown options={revenueMonth} value={selectedRevenueMonth} optionLabel="name" onChange={changeRevenueChart}></Dropdown>
                        </div>
                        <div className="graph">
                            <Chart ref={revenue} type="line" id="revenue-chart" data={revenueChart} options={revenueChartOptions}></Chart>
                        </div>
                    </div>
                </div>

                <div className="col-12 md:col-8">
                    <div className="card widget-table">
                        <div className="card-header">
                            <span>Yearly win</span>
                            <Dropdown options={orderYear} value={selectedOrderYear} optionLabel="name" onChange={recentSales}></Dropdown>
                        </div>
                        <DataTable className="p-datatable-customers" ref={dt} value={customersTable} dataKey="id" rowHover rows={10} paginator>
                            <Column field="representative.name" header="Agent" sortable body={representativeTemplate} style={{ minWidth: '14rem' }}></Column>
                            <Column field="country.name" header="Country" sortable body={countryTemplate} style={{ minWidth: '10rem' }}></Column>
                            <Column field="date" header="Date" sortable body={(data) => formatDate(data.date)}></Column>
                            <Column field="balance" header="Balance" sortable body={(data) => formatCurrency(data.balance)}></Column>
                            <Column headerStyle={{ width: '8em' }} bodyStyle={{ textAlign: 'center' }} body={actionTemplate}></Column>
                        </DataTable>
                    </div>
                </div>

                <div className="col-12 md:col-4">
                    <div className="card widget-performance">
                        <div className="header">
                            <span>Quarterly win</span>
                            <p className="subtitle">Top performances</p>
                        </div>
                        <div className="content">
                            <ul>
                                <li className="person-item">
                                    <Avatar image="assets/layout/images/dashboard/ann.png" className="mr-2 p-overlay-badge" shape="circle"><Badge value={1} /></Avatar>
                                    <div className="person-info"><div className="amount">$94,815</div><div className="name">Ann Vaccaro</div></div>
                                </li>
                                <li className="person-item">
                                    <Avatar image="assets/layout/images/dashboard/miracle.png" className="mr-2 p-overlay-badge" shape="circle"><Badge value={2} /></Avatar>
                                    <div className="person-info"><div className="amount">$78,985</div><div className="name">Miracle Aminoff</div></div>
                                </li>
                                <li className="person-item">
                                    <Avatar image="assets/layout/images/dashboard/kaylynn.png" className="mr-2 p-overlay-badge" shape="circle"><Badge value={3} /></Avatar>
                                    <div className="person-info"><div className="amount">$53,611</div><div className="name">Kaylynn Geidt</div></div>
                                </li>
                                <li className="person-item">
                                    <Avatar image="assets/layout/images/dashboard/angel.png" className="mr-2 p-overlay-badge" shape="circle"><Badge value={4} /></Avatar>
                                    <div className="person-info"><div className="amount">$25,338</div><div className="name">Angel Rosser</div></div>
                                </li>
                                <li className="person-item">
                                    <Avatar image="assets/layout/images/dashboard/cristofer.png" className="mr-2 p-overlay-badge" shape="circle"><Badge value={5} /></Avatar>
                                    <div className="person-info"><div className="amount">$15,989</div><div className="name">Cristofer Mango</div></div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="col-12 lg:col-8">
                    <div className="card widget-customer-graph">
                        <div className="header">
                            <div className="title">
                                <span>Weekly new customers</span>
                                <Dropdown options={customerYear} value={selectedCustomerYear} optionLabel="name" onChange={changeCustomerChart}></Dropdown>
                            </div>
                            <p className="subtitle">Number of new customer are listed by weekly</p>
                        </div>
                        <div className="content grid grid-nogutter">
                            <div className="col-12 md:col-6 grid">
                                <div className="col-12 md:col-4 flex align-items-center">
                                    <h2>{customerMax}</h2><p>MAX</p>
                                </div>
                                <div className="col-12 md:col-4 flex align-items-center">
                                    <h2>{customerMin}</h2><p>MIN</p>
                                </div>
                                <div className="col-12 md:col-4 flex align-items-center">
                                    <h2 style={{ color: '#FC6161' }}>{customerAvg}</h2><p>AVERAGE</p>
                                </div>
                            </div>
                        </div>
                        <Chart ref={customer} type="bar" id="customer-chart" data={customerChart} options={customerChartOptions}></Chart>
                    </div>
                </div>

                <div className="col-12 lg:col-4">
                    <div className="card widget-target">
                        <div className="card-header">
                            <span>Weekly target</span>
                        </div>
                        <div className="content">
                            <h3>1232 Users</h3>
                            <span className="rate">%3.5 <i className="pi pi-arrow-up"></i><span> than last week</span></span>
                        </div>
                        <div className="values">
                            <div className="item"><span>51%</span><ProgressBar value={51} showValue={false}></ProgressBar><span className="day">Thu</span></div>
                            <div className="item"><span>68%</span><ProgressBar value={68} showValue={false}></ProgressBar><span className="day">Fri</span></div>
                            <div className="item"><span>74%</span><ProgressBar value={74} showValue={false}></ProgressBar><span className="day">Sat</span></div>
                            <div className="item"><span>61%</span><ProgressBar value={61} showValue={false}></ProgressBar><span className="day">Sun</span></div>
                            <div className="item success"><span>100%</span><ProgressBar value={100} showValue={false}></ProgressBar><span className="day">Mon</span></div>
                            <div className="item"><span>70%</span><ProgressBar value={70} showValue={false}></ProgressBar><span className="day">Tue</span></div>
                            <div className="item today"><span>22%</span><ProgressBar value={22} showValue={false}></ProgressBar><span className="day">Today</span></div>
                        </div>
                    </div>
                </div> */}

                {/* ── Calendario de Reservas ── */}
                <div className="col-12" style={{ marginTop: '1.5rem' }}>
                    <div className="card" style={{ borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <div>
                                <h5 style={{ margin: 0, color: '#101828' }}>Reservas</h5>
                                <p style={{ margin: '0.2rem 0 0', color: '#667085', fontSize: '0.82rem' }}>Vista general de espacios reservados</p>
                            </div>
                            <Button
                                label="Ver todas"
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                text
                                size="small"
                                onClick={() => navigate(esAdmin ? '/reservasSUMAdmin' : '/reservarSum')}
                            />
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#667085', marginBottom: '0.6rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#1570ef', marginRight: 5 }} />Confirmada</span>
                            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#f04438', marginRight: 5 }} />Cancelada</span>
                        </div>
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin]}
                            initialView="timeGridWeek"
                            locale={esLocale}
                            firstDay={1}
                            headerToolbar={{
                                left:   'prev,next today',
                                center: 'title',
                                right:  'dayGridMonth,timeGridWeek,timeGridDay',
                            }}
                            buttonText={{ today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día' }}
                            allDayText="Todo el día"
                            noEventsText="Sin reservas"
                            slotMinTime="06:00:00"
                            slotMaxTime="23:00:00"
                            slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                            events={eventosDash}
                            height="auto"
                            editable={false}
                            selectable={false}
                            eventContent={(info) => {
                                const r = info.event.extendedProps.reserva;
                                const isMonth = info.view.type === 'dayGridMonth';
                                return (
                                    <div style={{ padding: isMonth ? '1px 4px' : '3px 6px', fontSize: isMonth ? '0.71rem' : '0.78rem', overflow: 'hidden', lineHeight: 1.35, cursor: 'default' }}>
                                        <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {r.salonNombre}
                                        </div>
                                        {!isMonth && (
                                            <>
                                                <div style={{ opacity: 0.92 }}>{r.solicitante}</div>
                                                {r.motivo && (
                                                    <div style={{ opacity: 0.75, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {r.motivo}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            }}
                        />
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Dashboard;
